// -- 03.11.26 claude: Wendeli-Rechenspiel fuer wendeli.ch. Das blaue Wendeli
// aus den Animationen (spiel/modell_bauen.py), dieselbe Spur wie im Video
// Treppe (animationen/src/treppe/Treppe.tsx): +1 ist eine Stufe auf der
// Treppenwange, +10 ein Stockwerk im Lift am Facettenrand.
//
// Einbinden:  <div data-wendeli-spiel data-modell="…/wendeli_spiel.glb"
//                  data-ton="…/ton/"></div>
//             <script type="module" src="…/wendeli-spiel.js"></script>
// data-ton ist entweder ein Ordner (endet auf /) oder ein JSON-Objekt
// {"lob_super_a.mp3": "https://…", …} - so liefert es die Shopify-Sektion.

import * as THREE from "https://cdn.jsdelivr.net/npm/three@0.170.0/build/three.module.js";

// ---------- Farben: das blaue Wendeli (animationen/src/figur/WendeliFigur.tsx) ----------
const k_farbe = {
  hintergrund: "#EDE7D8",
  rohr: "#25509E",
  fenster: "#F7F5EF",
  marke: "#EE8A2E",
  ziffer: "#1E3778",
  wange: "#EE8A2E",
  zwinge: "#9EA2A8",
  gummi: "#EFEEEA",
  mine: "#1C1C1E",
  spur: "#FFC21A",
  text: "#0E2450",
};

// ---------- Spielregeln ----------
const k_leuchten_ab = 1.0; // s auf dem Ziel, dann leuchtet es
const k_konfetti_ab = 3.0; // s auf dem Ziel, dann Konfetti und neue Aufgabe
const k_tipp_dauer = 3.6; // s, so lange steht die Sternenspur
const k_schritt_s = 0.26; // s je Stufe
const k_lift_s = 0.95; // s je Stockwerk: hineinhuepfen, fahren, hinaushuepfen
const k_lift_phasen = [0.2, 0.82]; // Anteile: bis hier hinein, bis hier fahren, dann hinaus
const k_swipe_px = 22; // so weit muss ein Wisch mindestens gehen
const k_tipp_px = 10; // weniger bewegt ist ein Tipp auf eine Zahl; dazwischen gilt nichts
const k_zoom = [0.35, 2.6]; // Kameraabstand mal Zoom: so nah und so weit darf es gehen

const k_stufen = {
  leicht: { name: "Leicht", text: "bis 10 Schritte" },
  mittel: { name: "Mittel", text: "Zehner und Einer" },
  schwer: { name: "Schwer", text: "zwei Rechnungen" },
};

const k_ton_lob = ["lob_super", "lob_weiter", "lob_toll", "lob_genau", "lob_spitze", "lob_juhu"];
const k_ton_alle = [...k_ton_lob, "lob_kurz", "start", "tipp", "halt"];

const k_teilung = Math.PI / 5; // 36 Grad je Facette
const k_knick = (35 * Math.PI) / 180; // wie animationen/src/montage/teile.ts

const zufall = (a, b) => a + Math.floor(Math.random() * (b - a + 1));
const klammer = (x, a, b) => Math.min(b, Math.max(a, x));
const minus = "−";

// ======================================================================
// Modell: GLB selbst lesen (nur Position und Index), Normalen hier rechnen.
// Dann braucht das Spiel nur three.js selbst und keinen Loader.
// ======================================================================

const glbLesen = (p_buf) => {
  const w_dv = new DataView(p_buf);
  if (w_dv.getUint32(0, true) !== 0x46546c67) throw new Error("keine GLB-Datei");
  let w_json = null;
  let w_bin = 0;
  for (let o = 12; o < p_buf.byteLength; ) {
    const w_len = w_dv.getUint32(o, true);
    const w_typ = w_dv.getUint32(o + 4, true);
    if (w_typ === 0x4e4f534a) w_json = JSON.parse(new TextDecoder().decode(new Uint8Array(p_buf, o + 8, w_len)));
    if (w_typ === 0x004e4942) w_bin = o + 8;
    o += 8 + w_len;
  }
  const w_typen = { 5126: Float32Array, 5125: Uint32Array, 5123: Uint16Array, 5121: Uint8Array };
  const zugriff = (p_i) => {
    const a = w_json.accessors[p_i];
    const v = w_json.bufferViews[a.bufferView];
    const w_n = a.count * (a.type === "VEC3" ? 3 : 1);
    const T = w_typen[a.componentType];
    // slice: kopieren, damit die Ausrichtung stimmt
    return new T(p_buf.slice(w_bin + (v.byteOffset || 0) + (a.byteOffset || 0), w_bin + (v.byteOffset || 0) + (a.byteOffset || 0) + w_n * T.BYTES_PER_ELEMENT));
  };
  const w_teile = {};
  for (const w_knoten of w_json.nodes) {
    if (w_knoten.mesh === undefined) continue;
    const w_prim = w_json.meshes[w_knoten.mesh].primitives[0];
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(zugriff(w_prim.attributes.POSITION), 3));
    if (w_prim.indices !== undefined) g.setIndex(new THREE.BufferAttribute(zugriff(w_prim.indices), 1));
    if (w_knoten.matrix) g.applyMatrix4(new THREE.Matrix4().fromArray(w_knoten.matrix));
    w_teile[w_knoten.name || w_json.meshes[w_knoten.mesh].name] = g;
  }
  return { teile: w_teile, masse: w_json.asset.extras.masse };
};

// Normalen mit Knick: flachere Kanten glatt, schaerfere bleiben Kanten
// (dasselbe wie toCreasedNormals in three/examples, hier ohne den Import)
const knickNormalen = (p_g, p_winkel) => {
  const g = p_g.index ? p_g.toNonIndexed() : p_g;
  const pos = g.attributes.position.array;
  const w_n = pos.length / 9;
  const w_fn = new Float32Array(w_n * 3);
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
  for (let f = 0; f < w_n; f++) {
    a.fromArray(pos, f * 9);
    b.fromArray(pos, f * 9 + 3);
    c.fromArray(pos, f * 9 + 6);
    c.sub(b);
    a.sub(b);
    c.cross(a).normalize();
    w_fn.set([c.x, c.y, c.z], f * 3);
  }
  const schluessel = (i) => `${Math.round(pos[i] * 1e3)},${Math.round(pos[i + 1] * 1e3)},${Math.round(pos[i + 2] * 1e3)}`;
  const w_karte = new Map();
  for (let i = 0; i < w_n * 3; i++) {
    const k = schluessel(i * 3);
    if (!w_karte.has(k)) w_karte.set(k, []);
    w_karte.get(k).push(Math.floor(i / 3));
  }
  const w_cos = Math.cos(p_winkel);
  const nor = new Float32Array(pos.length);
  for (let i = 0; i < w_n * 3; i++) {
    const f = Math.floor(i / 3);
    let x = 0, y = 0, z = 0;
    for (const h of w_karte.get(schluessel(i * 3))) {
      const d = w_fn[f * 3] * w_fn[h * 3] + w_fn[f * 3 + 1] * w_fn[h * 3 + 1] + w_fn[f * 3 + 2] * w_fn[h * 3 + 2];
      if (d > w_cos) {
        x += w_fn[h * 3];
        y += w_fn[h * 3 + 1];
        z += w_fn[h * 3 + 2];
      }
    }
    const l = Math.hypot(x, y, z) || 1;
    nor.set([x / l, y / l, z / l], i * 3);
  }
  g.setAttribute("normal", new THREE.BufferAttribute(nor, 3));
  return g;
};

// ======================================================================
// Aufgaben
// ======================================================================

// Eine Aufgabe ab p: Liste von Summanden, die das Kind sieht, und das Ziel
export const aufgabeErzeugen = (p_stufe, p_start, p_vorher) => {
  for (let i = 0; i < 500; i++) {
    let w_teile;
    const plus = Math.random() < 0.6 ? 1 : -1;
    if (p_stufe === "leicht") {
      // Kaspar 04.11.26: mehrheitlich Additionen, auch mit dem Lift (+10, +20,
      // +14); Subtraktionen nur ganz leichte, -1 bis -12
      if (Math.random() < 0.8) {
        const r = Math.random();
        w_teile = [r < 0.35 ? zufall(1, 9) : r < 0.6 ? 10 * zufall(1, 2) : zufall(11, 19)];
      } else w_teile = [-zufall(1, 12)];
    } else if (p_stufe === "mittel") {
      // Zehner und Einer, z.B. 34 + 23
      w_teile = [plus * (10 * zufall(1, 4) + zufall(Math.random() < 0.2 ? 0 : 1, 9))];
    } else if (Math.random() < 0.5) {
      // schwer: ein grosser Sprung mit vielen Einern, da lohnt der Umweg (+29 = +30 - 1)
      w_teile = [plus * (10 * zufall(2, 6) + zufall(5, 9))];
    } else {
      // schwer: zwei Rechnungen hintereinander, z.B. 47 + 35 - 8
      w_teile = [plus * (10 * zufall(1, 4) + zufall(1, 9)), (Math.random() < 0.5 ? 1 : -1) * zufall(3, 19)];
    }
    // Jeder Zwischenstand der Rechnung bleibt in 0..100
    let w_p = p_start;
    let w_gut = true;
    for (const t of w_teile) {
      w_p += t;
      if (w_p < 0 || w_p > 100) w_gut = false;
    }
    if (!w_gut || w_p === p_start) continue;
    const w_text = `${p_start} ` + w_teile.map((t) => (t > 0 ? `+ ${t}` : `${minus} ${-t}`)).join(" ");
    if (p_vorher && w_text === p_vorher.text) continue;
    return { start: p_start, teile: w_teile, ziel: w_p, text: w_text };
  }
  // Notausgang, falls nichts passt (sollte nie vorkommen)
  const w_ziel = p_start < 50 ? p_start + 1 : p_start - 1;
  return { start: p_start, teile: [w_ziel - p_start], ziel: w_ziel, text: `${p_start} ${w_ziel > p_start ? "+ 1" : `${minus} 1`}` };
};

// Kuerzester Weg von a nach b: Zehner und Einer mit dem wenigsten Zuegen,
// und so geordnet, dass er 0..100 nie verlaesst. Zuege: +10 -10 +1 -1.
export const kuerzesterWeg = (p_von, p_nach) => {
  const d = p_nach - p_von;
  const w_k0 = Math.round(d / 10);
  const w_kand = [];
  for (let k = w_k0 - 2; k <= w_k0 + 2; k++) w_kand.push({ k, e: d - 10 * k, kosten: Math.abs(k) + Math.abs(d - 10 * k) });
  w_kand.sort((x, y) => x.kosten - y.kosten || Math.abs(x.k) - Math.abs(y.k));
  for (const { k, e } of w_kand) {
    const z = Array(Math.abs(k)).fill(Math.sign(k) * 10);
    const ei = Array(Math.abs(e)).fill(Math.sign(e));
    // Erst die Lifte, sonst erst die Stufen; am Rand ein Teil der Stufen davor
    // (1 -> 97: -1, zehnmal Lift, -3)
    const w_teilungen = [0, ei.length, ...Array.from({ length: Math.max(0, ei.length - 1) }, (_, j) => j + 1)];
    for (const j of w_teilungen) {
      const w_zuege = [...ei.slice(0, j), ...z, ...ei.slice(j)];
      let p = p_von;
      if (w_zuege.every((m) => (p += m) >= 0 && p <= 100)) return w_zuege;
    }
  }
  return [];
};

// Der Weg beim Antippen: erst alle Lifte, dann die Stufen, beide in Richtung
// des Ziels (38 -> 61: +10 +10 +1 +1 +1). Weil beide Teile dasselbe Vorzeichen
// haben, bleibt jeder Zwischenstand zwischen Start und Ziel, also in 0..100.
export const wegLiftZuerst = (p_von, p_nach) => {
  const d = p_nach - p_von;
  const k = Math.trunc(d / 10);
  const e = d - 10 * k;
  return [...Array(Math.abs(k)).fill(Math.sign(k) * 10), ...Array(Math.abs(e)).fill(Math.sign(e))];
};

// Wie viele Zuege braucht, wer nur die Ziffern abzaehlt (+19 = 1 Lift, 9 Stufen)?
const ziffernZuege = (d) => Math.floor(Math.abs(d) / 10) + (Math.abs(d) % 10);

// ======================================================================
// Ton: Stimme des blauen Wendeli (ElevenLabs, Dylan Carter), dazu
// kleine Schritttoene aus WebAudio
// ======================================================================

class Ton {
  constructor(p_quelle) {
    this.quelle = p_quelle;
    this.stumm = false;
    this.cache = {};
    this.laeuft = null;
    this.letztes = {};
    this.ctx = null;
  }
  url(p_datei) {
    if (typeof this.quelle === "object" && this.quelle) return this.quelle[p_datei];
    return (this.quelle || "") + p_datei;
  }
  // Die Stimme laeuft ueber den AudioContext und nicht ueber <audio>: iOS gibt
  // jedes Audio-Element einzeln frei, und das Lob kommt 3 s nach dem letzten
  // Wisch, also ausserhalb jeder Geste. Ein einmal freigegebener Context spielt.
  entsperren() {
    if (!this.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) this.ctx = new AC();
    }
    this.weiter();
    for (const w of k_ton_alle) for (const t of ["a", "b"]) this.holen(`${w}_${t}.mp3`);
  }
  // Nach Anruf, App-Wechsel oder Sperre steht der Context still; jede Geste holt ihn zurueck
  weiter() {
    if (this.ctx && this.ctx.state !== "running") this.ctx.resume().catch(() => {});
  }
  holen(p_datei) {
    if (!this.cache[p_datei] && this.ctx) {
      const u = this.url(p_datei);
      if (!u) return null;
      this.cache[p_datei] = fetch(u)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(r.status))))
        .then((b) => new Promise((ok, nein) => this.ctx.decodeAudioData(b, ok, nein)))
        .catch(() => null);
    }
    return this.cache[p_datei] || null;
  }
  stoppen() {
    if (this.laeuft) {
      try {
        this.laeuft.stop();
      } catch {
        /* schon zu Ende */
      }
      this.laeuft = null;
    }
  }
  sprechen(p_satz, p_sperre_s = 0) {
    if (this.stumm || !this.ctx) return;
    const jetzt = performance.now() / 1000;
    if (p_sperre_s && this.letztes[p_satz] && jetzt - this.letztes[p_satz] < p_sperre_s) return;
    this.letztes[p_satz] = jetzt;
    const w_laden = this.holen(`${p_satz}_${Math.random() < 0.5 ? "a" : "b"}.mp3`);
    if (!w_laden) return;
    w_laden.then((p_buf) => {
      // Noch nicht geladen und schon ueberholt? Dann lieber schweigen als verspaetet reden
      if (!p_buf || this.stumm || performance.now() / 1000 - jetzt > 1.5) return;
      this.stoppen();
      const q = this.ctx.createBufferSource();
      q.buffer = p_buf;
      q.connect(this.ctx.destination);
      q.start();
      this.laeuft = q;
    });
  }
  piep(p_freq, p_dauer = 0.08, p_typ = "sine", p_laut = 0.06) {
    if (this.stumm || !this.ctx) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = p_typ;
    o.frequency.setValueAtTime(p_freq, t);
    g.gain.setValueAtTime(p_laut, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + p_dauer);
    o.connect(g).connect(this.ctx.destination);
    o.start(t);
    o.stop(t + p_dauer + 0.02);
  }
  schritt(p_zug) {
    if (Math.abs(p_zug) === 10) {
      // Lift: ein kleiner Gleitton
      if (this.stumm || !this.ctx) return;
      const t = this.ctx.currentTime;
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.frequency.setValueAtTime(p_zug > 0 ? 330 : 660, t);
      o.frequency.exponentialRampToValueAtTime(p_zug > 0 ? 660 : 330, t + 0.3);
      g.gain.setValueAtTime(0.05, t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.34);
      o.connect(g).connect(this.ctx.destination);
      o.start(t);
      o.stop(t + 0.36);
    } else this.piep(p_zug > 0 ? 880 : 620, 0.07, "triangle", 0.07);
  }
}

// ======================================================================
// Das Spiel
// ======================================================================

const k_css = `
.wsp{position:relative;width:100%;height:100%;min-height:420px;overflow:hidden;background:${k_farbe.hintergrund};
  font-family:Arial,Helvetica,sans-serif;color:${k_farbe.text};user-select:none;-webkit-user-select:none;
  touch-action:pan-y;outline:none;-webkit-tap-highlight-color:transparent;border-radius:inherit;container:wsp / inline-size}
.wsp.aktiv{touch-action:none}
.wsp canvas{display:block;width:100%;height:100%}
.wsp button{font-family:inherit;color:inherit;cursor:pointer;-webkit-tap-highlight-color:transparent}
.wsp-oben{position:absolute;left:0;right:0;top:0;display:flex;justify-content:space-between;align-items:center;padding:10px 12px;gap:8px;pointer-events:none}
.wsp-oben>*{pointer-events:auto}
.wsp-stufen{display:flex;gap:4px;background:rgba(255,255,255,.55);border-radius:999px;padding:3px}
.wsp-stufen button{border:0;background:transparent;border-radius:999px;padding:7px 12px;font-size:14px;font-weight:700;opacity:.65}
.wsp-stufen button.an{background:${k_farbe.rohr};color:#fff;opacity:1}
.wsp-rechts{display:flex;gap:6px;align-items:center}
.wsp-sterne{font-weight:700;font-size:16px;background:rgba(255,255,255,.55);border-radius:999px;padding:7px 12px;white-space:nowrap}
.wsp-rund{border:0;background:rgba(255,255,255,.55);border-radius:999px;width:36px;height:36px;font-size:17px;line-height:36px;padding:0}
.wsp-aufgabe{position:absolute;left:50%;top:58px;transform:translateX(-50%);background:rgba(245,241,230,.93);
  border-radius:28px;padding:8px 26px 10px;text-align:center;box-shadow:0 2px 10px rgba(14,36,80,.12);white-space:nowrap;pointer-events:none}
.wsp-aufgabe small{display:block;font-size:13px;font-weight:700;opacity:.6;letter-spacing:.04em}
.wsp-aufgabe b{display:block;font-size:clamp(30px,8vw,48px);line-height:1.1}
.wsp-aufgabe.neu{animation:wsp-neu .55s ease-out}
@keyframes wsp-neu{0%{transform:translateX(-50%) scale(.6);opacity:0}70%{transform:translateX(-50%) scale(1.08);opacity:1}100%{transform:translateX(-50%) scale(1)}}
.wsp-stand{position:absolute;left:50%;bottom:34px;transform:translateX(-50%);text-align:center;pointer-events:none;
  background:rgba(245,241,230,.93);border-radius:28px;padding:6px 26px 6px;min-width:90px;box-shadow:0 2px 10px rgba(14,36,80,.12)}
.wsp-stand b{display:block;font-size:clamp(40px,11vw,64px);line-height:1}
.wsp-stand span{display:block;font-size:17px;font-weight:700;margin-top:2px}
.wsp-stand span:empty{display:none}
.wsp-stand i{font-style:normal}
.wsp-stand .z{color:${k_farbe.rohr}}
.wsp-stand .e{color:#C96A12}
.wsp-stand small{display:block;font-size:12px;font-weight:700;opacity:.55;letter-spacing:.05em}
.wsp-stand.puls b{animation:wsp-puls .25s ease-out}
@keyframes wsp-puls{0%{transform:scale(1.18)}100%{transform:scale(1)}}
.wsp-seite{position:absolute;right:12px;top:50%;transform:translateY(-50%);display:flex;flex-direction:column;align-items:center;gap:10px}
.wsp-tipp{position:relative;border:0;width:58px;height:58px;border-radius:50%;margin-bottom:16px;
  background:#FFC21A;box-shadow:0 3px 10px rgba(14,36,80,.25);font-size:26px;line-height:58px;padding:0}
.wsp-tipp span{position:absolute;left:-10px;right:-10px;top:60px;font-size:12px;font-weight:700;line-height:1.2}
.wsp-tipp:active,.wsp-seite .wsp-rund:active{transform:scale(.94)}
.wsp-seite .wsp-rund{width:40px;height:40px;line-height:40px;font-size:22px;font-weight:700;background:rgba(255,255,255,.75);
  box-shadow:0 1px 4px rgba(14,36,80,.2)}
.wsp-menue{position:absolute;top:54px;right:12px;background:#FBF9F3;border-radius:16px;padding:12px 14px;min-width:230px;
  box-shadow:0 6px 24px rgba(14,36,80,.25);text-align:left;font-size:14px;display:none;z-index:5}
.wsp-menue.offen{display:block}
.wsp-menue b{display:block;font-size:12px;letter-spacing:.05em;opacity:.6;margin:2px 0 6px}
.wsp-menue button{display:block;width:100%;text-align:left;border:0;background:transparent;border-radius:10px;padding:8px 10px;font-size:15px;margin-bottom:2px}
.wsp-menue button.an{background:${k_farbe.rohr};color:#fff}
.wsp-menue button small{opacity:.75;margin-left:4px}
.wsp-menue hr{border:0;border-top:1px solid rgba(14,36,80,.12);margin:8px 0}
.wsp.schauen .wsp-aufgabe,.wsp.schauen .wsp-stand,.wsp.schauen .wsp-tipp,.wsp.schauen .wsp-sterne{display:none}
.wsp:fullscreen{border-radius:0}
.wsp.vollbild{position:fixed;inset:0;width:100vw;height:100vh;height:100dvh;z-index:2147483000;border-radius:0}
.wsp-hilfe{position:absolute;left:50%;bottom:6px;transform:translateX(-50%);text-align:center;font-size:12px;pointer-events:none;
  font-weight:700;background:rgba(245,241,230,.8);border-radius:999px;padding:2px 10px;white-space:nowrap}
.wsp-hilfe .wisch{display:none}
@media (hover:none) and (pointer:coarse){.wsp-hilfe .taste{display:none}.wsp-hilfe .wisch{display:inline}}
.wsp-seite .wsp-null{font-size:15px}
.wsp-konfetti{position:absolute;inset:0;pointer-events:none}
.wsp-start{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;
  background:rgba(237,231,216,.86);text-align:center;padding:16px}
.wsp-start h2{margin:0;font-size:clamp(24px,6vw,34px);font-family:inherit;font-weight:700;color:inherit;
  letter-spacing:normal;text-transform:none;line-height:1.2}
.wsp p{color:inherit;font-family:inherit}
.wsp-start p{margin:0;font-size:15px;max-width:30em;line-height:1.4}
.wsp-tasten{display:grid;grid-template-columns:repeat(2,auto);gap:6px 16px;font-size:15px;font-weight:700;text-align:left}
.wsp-tasten i{font-style:normal;display:inline-block;width:30px;height:30px;line-height:30px;text-align:center;border-radius:8px;
  background:#fff;box-shadow:0 1px 3px rgba(14,36,80,.25);margin-right:6px}
.wsp-wahl{display:flex;gap:10px;flex-wrap:wrap;justify-content:center}
.wsp-wahl button{border:0;border-radius:18px;padding:12px 18px;background:${k_farbe.rohr};color:#fff;font-size:18px;font-weight:700;min-width:110px}
.wsp-wahl button small{display:block;font-size:12px;font-weight:400;opacity:.85}
.wsp-wahl button:disabled{opacity:.45;cursor:wait}
.wsp-laden{font-size:13px;opacity:.7;min-height:16px}
.wsp-meldung{position:absolute;left:50%;top:42%;transform:translate(-50%,-50%);font-size:clamp(28px,8vw,44px);font-weight:700;
  color:#fff;text-shadow:0 2px 8px rgba(14,36,80,.5);pointer-events:none;opacity:0;transition:opacity .3s;white-space:nowrap}
.wsp-meldung.an{opacity:1}
@container wsp (max-width:420px){.wsp-stufen button{padding:6px 8px;font-size:13px}.wsp-sterne{padding:6px 8px;font-size:14px}.wsp-oben{padding:8px 6px;gap:4px}.wsp-aufgabe{top:56px}.wsp-tipp{width:52px;height:52px;line-height:52px}.wsp-seite{right:8px;gap:8px}
  .wsp-seite .wsp-rund{width:36px;height:36px;line-height:36px;font-size:19px}.wsp-seite .wsp-null{font-size:14px}}
@container wsp (max-width:330px){.wsp-stufen button{padding:5px 6px;font-size:12px}.wsp-sterne{display:none}}
@media (prefers-reduced-motion:reduce){.wsp-aufgabe.neu,.wsp-stand.puls b{animation:none}}
`;

export class WendeliSpiel {
  constructor(p_el, p_opt = {}) {
    this.el = p_el;
    this.opt = p_opt;
    this.ton = new Ton(p_opt.ton);
    this.stufe = "leicht";
    this.punkte = this.laden();
    this.laeuft = false;
    this.bauenDom();
    this.bauenSzene();
    this.modellLaden(p_opt.modell);
  }

  // Aufraeumen, wenn die Sektion verschwindet (Shopify-Theme-Editor)
  zerstoeren() {
    this.renderer?.setAnimationLoop(null);
    this.beobachterGroesse?.disconnect();
    this.beobachterSicht?.disconnect();
    this.renderer?.dispose();
    this.ton.stoppen();
    this.ton.ctx?.close().catch(() => {});
    if (this.wurzel.classList.contains("vollbild")) document.documentElement.style.overflow = this.altesOverflow || "";
    document.removeEventListener("fullscreenchange", this.beiVollbild);
    document.removeEventListener("webkitfullscreenchange", this.beiVollbild);
    this.wurzel.remove();
  }

  // ---------- Bestand: geloeste Aufgaben je Stufe, nur in diesem Browser ----------
  laden() {
    try {
      return { leicht: 0, mittel: 0, schwer: 0, ...JSON.parse(localStorage.getItem("wendeli-spiel") || "{}") };
    } catch {
      return { leicht: 0, mittel: 0, schwer: 0 };
    }
  }
  speichern() {
    try {
      localStorage.setItem("wendeli-spiel", JSON.stringify(this.punkte));
    } catch {
      /* privates Fenster: dann eben ohne */
    }
  }

  // ---------- Oberflaeche ----------
  bauenDom() {
    if (!document.getElementById("wsp-stil")) {
      const s = document.createElement("style");
      s.id = "wsp-stil";
      s.textContent = k_css;
      document.head.appendChild(s);
    }
    const w = document.createElement("div");
    w.className = "wsp";
    w.tabIndex = 0;
    w.setAttribute("role", "application");
    w.setAttribute("aria-label", "Wendeli-Rechenspiel: Pfeiltasten oder Wischen. Hoch plus zehn, runter minus zehn, rechts plus eins, links minus eins.");
    w.innerHTML = `
      <canvas></canvas>
      <canvas class="wsp-konfetti"></canvas>
      <div class="wsp-oben">
        <div class="wsp-stufen">${Object.entries(k_stufen).map(([k, v]) => `<button data-stufe="${k}">${v.name}</button>`).join("")}<button data-stufe="anschauen" title="Anschauen: drehen und zoomen" aria-label="Anschauen">👁</button></div>
        <div class="wsp-rechts"><div class="wsp-sterne" title="gelöste Aufgaben">★ 0</div><button class="wsp-rund wsp-rad" aria-label="Einstellungen" title="Einstellungen">⚙</button></div>
      </div>
      <div class="wsp-menue" role="dialog" aria-label="Einstellungen">
        <b>ZAHLEN</b>
        <button data-dezimal="0">0 … 100 <small>ganze Zahlen</small></button>
        <button data-dezimal="1">0.0 … 10.0 <small>Zehntel</small></button>
        <button data-dezimal="2">0.00 … 1.00 <small>Hundertstel</small></button>
        <hr><b>TON</b>
        <button class="wsp-laut"></button>
      </div>
      <div class="wsp-aufgabe"><small>RECHNE</small><b></b></div>
      <div class="wsp-meldung"></div>
      <div class="wsp-stand"><small>GELAUFEN</small><b>0</b><span></span></div>
      <div class="wsp-seite">
        <button class="wsp-tipp" aria-label="Tipp: kürzesten Weg zeigen">💡<span>Tipp</span></button>
        <button class="wsp-rund wsp-naeher" aria-label="Näher heran" title="Näher heran">+</button>
        <button class="wsp-rund wsp-weiter" aria-label="Weiter weg" title="Weiter weg">${minus}</button>
        <button class="wsp-rund wsp-voll" aria-label="Vollbild" title="Vollbild">⛶</button>
        <button class="wsp-rund wsp-null" aria-label="Zurück auf 0, neu anfangen" title="Zurück auf 0">↺0</button>
      </div>
      <div class="wsp-hilfe"></div>
      <div class="wsp-start">
        <h2>Bereit zum Rechnen?</h2>
        <p>Lauf auf dem Wendeli zum Ergebnis! Die Treppe geht Stufe für Stufe, der Lift fährt einen ganzen Zehner.
           Bleib auf dem Ergebnis stehen – stimmt es, leuchtet es.</p>
        <div class="wsp-tasten"></div>
        <p style="font-size:13px;opacity:.75">Mit den Pfeiltasten – oder wischen und das Wendeli ziehen:<br>
           nach unten ziehen = Lift hoch, nach links ziehen = Stufe hoch.<br>
           Oder direkt auf eine Zahl tippen: dann fährt es erst Lift, dann Treppe.</p>
        <div class="wsp-wahl">${Object.entries(k_stufen).map(([k, v]) => `<button data-start="${k}" disabled>${v.name}<small>${v.text}</small></button>`).join("")}<button data-start="anschauen" disabled>👁 Anschauen<small>drehen und zoomen</small></button></div>
        <div class="wsp-laden">Wendeli wird geladen …</div>
      </div>`;
    this.el.appendChild(w);
    this.wurzel = w;
    const $ = (s) => w.querySelector(s);
    this.dom = {
      canvas: w.querySelector("canvas"),
      konfetti: $(".wsp-konfetti"),
      stufen: [...w.querySelectorAll("[data-stufe]")],
      sterne: $(".wsp-sterne"),
      laut: $(".wsp-laut"),
      aufgabe: $(".wsp-aufgabe"),
      aufgabeText: $(".wsp-aufgabe b"),
      stand: $(".wsp-stand"),
      standZahl: $(".wsp-stand b"),
      standTeile: $(".wsp-stand span"),
      tipp: $(".wsp-tipp"),
      start: $(".wsp-start"),
      wahl: [...w.querySelectorAll("[data-start]")],
      laden: $(".wsp-laden"),
      meldung: $(".wsp-meldung"),
      hilfe: $(".wsp-hilfe"),
      tasten: $(".wsp-tasten"),
      menue: $(".wsp-menue"),
      rad: $(".wsp-rad"),
      dezimal: [...w.querySelectorAll("[data-dezimal]")],
      voll: $(".wsp-voll"),
    };
    for (const b of this.dom.wahl) b.addEventListener("click", () => this.starten(b.dataset.start));
    for (const b of this.dom.stufen) b.addEventListener("click", () => this.stufeWechseln(b.dataset.stufe));
    this.dom.tipp.addEventListener("click", () => this.tippZeigen());
    w.querySelector(".wsp-null").addEventListener("click", () => this.aufNull());
    // Ton: im Zahnrad-Menue, damit oben Platz bleibt
    this.dom.laut.addEventListener("click", () => {
      this.ton.stumm = !this.ton.stumm;
      if (this.ton.stumm) this.ton.stoppen();
      this.einstellungenAnzeigen();
      this.einstellungenSpeichern();
    });
    // Zahnrad: Menue auf und zu; ein Klick daneben schliesst es
    this.dom.rad.addEventListener("click", () => this.dom.menue.classList.toggle("offen"));
    for (const b of this.dom.dezimal)
      b.addEventListener("click", () => {
        this.dezimalSetzen(+b.dataset.dezimal);
        this.einstellungenSpeichern();
      });
    // Zoom: Knoepfe, Pinch mit zwei Fingern, am PC Ctrl+Rad (so meldet auch das Trackpad sein Pinch)
    w.querySelector(".wsp-naeher").addEventListener("click", () => this.zoomen(1 / 1.25));
    w.querySelector(".wsp-weiter").addEventListener("click", () => this.zoomen(1.25));
    w.addEventListener(
      "wheel",
      (e) => {
        if (!e.ctrlKey) return; // ein gewoehnliches Rad scrollt die Seite
        e.preventDefault();
        // eine Mausraste liefert rund 100, ein Trackpad kleine Schritte: begrenzt wie ein Knopfdruck
        const d = e.deltaMode === 1 ? e.deltaY * 40 : e.deltaY;
        this.zoomen(Math.exp(klammer(d, -60, 60) * 0.004));
      },
      { passive: false },
    );
    // Safari meldet das Trackpad-Pinch nicht als Rad, sondern als Geste
    let w_geste = 1;
    w.addEventListener("gesturestart", (e) => {
      if (!this.laeuft) return;
      e.preventDefault();
      w_geste = this.zoom;
    });
    w.addEventListener("gesturechange", (e) => {
      if (!this.laeuft) return;
      e.preventDefault();
      this.zoom = klammer(w_geste / (e.scale || 1), k_zoom[0], k_zoom[1]);
    });
    this.dom.voll.addEventListener("click", () => this.vollbild());
    this.beiVollbild = () => this.vollbildAnzeigen();
    document.addEventListener("fullscreenchange", this.beiVollbild);
    document.addEventListener("webkitfullscreenchange", this.beiVollbild);

    // Tasten: nur wenn das Spiel den Fokus hat und im Bild ist, sonst scrollt die Seite wie gewohnt
    w.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && w.classList.contains("vollbild")) return this.vollbild();
      if ((e.key === "+" || e.key === "-") && this.laeuft && !e.ctrlKey && !e.metaKey && !e.altKey) return this.zoomen(e.key === "+" ? 1 / 1.25 : 1.25);
      const z = { ArrowUp: 10, ArrowDown: -10, ArrowRight: 1, ArrowLeft: -1, w: 10, s: -10, d: 1, a: -1 }[e.key];
      if (z === undefined || !this.laeuft || !this.sichtbar) return;
      e.preventDefault();
      this.ton.weiter();
      // Anschauen: Pfeile drehen um eine Facette oder heben die Kamera um einen Umgang
      if (this.schauen) return this.freiSchieben(Math.abs(z) === 1 ? z : 0, Math.abs(z) === 10 ? Math.sign(z) * this.M.k_umgang_z : 0);
      if (!e.repeat || this.zuege.length < 2) this.zug(z);
    });
    // Wischen, Ziehen und Pinch, mit Finger oder Maus
    let w_ab = null;
    const w_finger = new Map();
    let w_pinch = null;
    const fingerWeg = (e) => {
      w_finger.delete(e.pointerId);
      if (w_finger.size < 2) w_pinch = null;
    };
    w.addEventListener("pointerdown", (e) => {
      this.ton.weiter();
      // Ein Tipp bei offenem Menue schliesst nur das Menue; im Menue selbst laeuft nichts los
      const w_menue_offen = this.dom.menue.classList.contains("offen");
      if (!e.target.closest(".wsp-menue, .wsp-rad")) this.dom.menue.classList.remove("offen");
      if (e.target.closest("button, .wsp-menue") || w_menue_offen) return;
      w.focus({ preventScroll: true });
      // Den Zeiger festhalten: sonst bleibt eine Maus, die ausserhalb losgelassen wird, als Finger haengen
      try {
        w.setPointerCapture(e.pointerId);
      } catch {
        /* alte Browser */
      }
      w_finger.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (w_finger.size === 2) {
        // zweiter Finger: ab jetzt Pinch, kein Wisch und kein Tipp
        const [p, q] = [...w_finger.values()];
        w_pinch = { abstand: Math.hypot(p.x - q.x, p.y - q.y) || 1, zoom: this.zoom };
        w_ab = null;
        return;
      }
      if (w_finger.size === 1) w_ab = { x: e.clientX, y: e.clientY, id: e.pointerId, lx: e.clientX, ly: e.clientY };
    });
    w.addEventListener("pointermove", (e) => {
      if (!w_finger.has(e.pointerId)) return;
      if (e.pointerType === "mouse" && !e.buttons) {
        // Taste ausserhalb losgelassen und nichts gemeldet: wie losgelassen behandeln
        fingerWeg(e);
        w_ab = null;
        return;
      }
      w_finger.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (w_pinch && w_finger.size >= 2) {
        const [p, q] = [...w_finger.values()];
        this.zoom = klammer((w_pinch.zoom * w_pinch.abstand) / (Math.hypot(p.x - q.x, p.y - q.y) || 1), k_zoom[0], k_zoom[1]);
        return;
      }
      // Anschauen: das Wendeli haengt am Finger
      if (this.schauen && w_ab && e.pointerId === w_ab.id) {
        const w_mm = (2 * this.kameraStand.abstand * Math.tan((15 * Math.PI) / 180)) / (this.wurzel.clientHeight || 1);
        this.freiSchieben((-(e.clientX - w_ab.lx) * w_mm) / (2 * this.halb), (e.clientY - w_ab.ly) * w_mm, true);
        w_ab.lx = e.clientX;
        w_ab.ly = e.clientY;
      }
    });
    for (const w_typ of ["pointercancel", "lostpointercapture"])
      w.addEventListener(w_typ, (e) => {
        fingerWeg(e);
        if (w_ab && e.pointerId === w_ab.id && w_typ === "pointercancel") w_ab = null;
      });
    const loslassen = (e) => {
      fingerWeg(e);
      if (!w_ab || e.pointerId !== w_ab.id) return;
      const dx = e.clientX - w_ab.x, dy = e.clientY - w_ab.y;
      w_ab = null;
      if (!this.laeuft || this.schauen) return;
      const w_weit = Math.max(Math.abs(dx), Math.abs(dy));
      // kaum bewegt: ein Tipp oder Klick auf eine Zahl, dorthin laufen
      if (w_weit < k_tipp_px) return this.zahlAntippen(e.clientX, e.clientY);
      if (w_weit < k_swipe_px) return;
      // Wischen zieht das Wendeli wie eine Seite (Kaspar 04.11.26): nach unten
      // ziehen bringt die hoeheren Zahlen ins Bild, also +10; nach links +1
      this.zug(Math.abs(dy) > Math.abs(dx) ? (dy > 0 ? 10 : -10) : dx < 0 ? 1 : -1);
    };
    w.addEventListener("pointerup", loslassen);
    this.zoom = 1;
    this.einstellungenLaden();
    this.stufeAnzeigen();
  }

  // ---------- Einstellungen: Dezimal-Modus und Ton, nur in diesem Browser ----------
  einstellungenLaden() {
    let w = {};
    try {
      w = JSON.parse(localStorage.getItem("wendeli-spiel-einstellungen") || "{}");
    } catch {
      /* privates Fenster */
    }
    this.ton.stumm = !!w.stumm;
    this.dezimalSetzen([0, 1, 2].includes(w.dezimal) ? w.dezimal : 0);
  }
  einstellungenSpeichern() {
    try {
      localStorage.setItem("wendeli-spiel-einstellungen", JSON.stringify({ dezimal: this.dezimal, stumm: this.ton.stumm }));
    } catch {
      /* privates Fenster */
    }
  }
  einstellungenAnzeigen() {
    for (const b of this.dom.dezimal) b.classList.toggle("an", +b.dataset.dezimal === this.dezimal);
    this.dom.laut.textContent = this.ton.stumm ? "🔇 Ton ist aus – einschalten" : "🔊 Ton ist an – ausschalten";
  }

  // Dezimal-Modus (Kaspar 04.11.26): dasselbe Wendeli zeigt 0 … 10.0 oder 0 … 1.00.
  // Gerechnet wird weiter in ganzen Stufen; nur Anzeige und Fensterzahlen wechseln.
  zahl(n) {
    return this.dezimal ? (n / 10 ** this.dezimal).toFixed(this.dezimal) : `${n}`;
  }
  mitVorzeichen(v) {
    return v > 0 ? `+${this.zahl(v)}` : v < 0 ? `${minus}${this.zahl(-v)}` : this.zahl(0);
  }
  aufgabeText(a) {
    return `${this.zahl(a.start)} ` + a.teile.map((t) => (t > 0 ? `+ ${this.zahl(t)}` : `${minus} ${this.zahl(-t)}`)).join(" ");
  }
  dezimalSetzen(p_stellen) {
    this.dezimal = p_stellen;
    this.einstellungenAnzeigen();
    this.hilfeAktualisieren();
    if (this.aufgabe) this.dom.aufgabeText.textContent = this.aufgabeText(this.aufgabe);
    if (this.gelaufen) this.standZeigen(false);
    if (this.bereit) this.zahlenAnwenden();
  }
  hilfeAktualisieren() {
    const z = this.zahl(10), e = this.zahl(1);
    if (this.schauen) {
      this.dom.hilfe.innerHTML = `ziehen oder Pfeiltasten: drehen und hoch/runter · + ${minus} zoomen`;
    } else {
      this.dom.hilfe.innerHTML =
        `<span class="taste">↑ +${z} · ↓ ${minus}${z} · → +${e} · ← ${minus}${e}</span>` +
        `<span class="wisch">wischen: ↓ +${z} · ↑ ${minus}${z} · ← +${e} · → ${minus}${e}</span>`;
    }
    this.dom.tasten.innerHTML =
      `<span><i>↑</i>Lift hoch: +${z}</span><span><i>→</i>Stufe hoch: +${e}</span>` +
      `<span><i>↓</i>Lift runter: ${minus}${z}</span><span><i>←</i>Stufe runter: ${minus}${e}</span>`;
  }

  // Die Fensterzahlen im Dezimal-Modus: eine Textur mit allen 101 Zahlen, je ein
  // Rechteck vor jedem Fenster. Die Ziffern des Modells bekommen die Farbe des
  // Fensters - ausblenden geht nicht, sie fuellen die Loecher in den Fenstern.
  zahlenAnwenden() {
    if (!this.zahlenNetz && this.dezimal) this.zahlenBauen();
    if (this.zahlenNetz) {
      this.zahlenNetz.visible = !!this.dezimal;
      if (this.dezimal) this.zahlenZeichnen();
    }
    this.zifferMat.color.set(this.dezimal ? k_farbe.fenster : k_farbe.ziffer);
  }
  zahlenBauen() {
    const k_spalten = 11, k_zellB = 186, k_zellH = 102; // 11 x 10 Zellen auf 2048 x 1024
    this.zahlenBild = document.createElement("canvas");
    this.zahlenBild.width = 2048;
    this.zahlenBild.height = 1024;
    const w_b = 9.6, w_h = (w_b * k_zellH) / k_zellB; // mm, passt in das Zehneck zwischen den Marken
    const r = this.M.k_r_inkreis + 0.03;
    const pos = [], uv = [], idx = [];
    for (let n = 0; n <= 100; n++) {
      const phi = n * k_teilung, y = this.fensterY(n);
      const c = new THREE.Vector3(r * Math.cos(phi), y, -r * Math.sin(phi));
      const T = new THREE.Vector3(-Math.sin(phi), 0, -Math.cos(phi)); // +u, auf dem Bild nach rechts
      const ecke = (su, sv) => c.clone().addScaledVector(T, (su * w_b) / 2).add(new THREE.Vector3(0, (sv * w_h) / 2, 0));
      const u0 = ((n % k_spalten) * k_zellB) / 2048, u1 = u0 + k_zellB / 2048;
      const v1 = 1 - (Math.floor(n / k_spalten) * k_zellH) / 1024, v0 = v1 - k_zellH / 1024;
      const i = pos.length / 3;
      for (const [su, sv, uu, vv] of [[-1, -1, u0, v0], [1, -1, u1, v0], [1, 1, u1, v1], [-1, 1, u0, v1]]) {
        pos.push(...ecke(su, sv).toArray());
        uv.push(uu, vv);
      }
      idx.push(i, i + 1, i + 2, i, i + 2, i + 3);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
    g.setIndex(idx);
    this.zahlenTextur = new THREE.CanvasTexture(this.zahlenBild);
    this.zahlenTextur.colorSpace = THREE.SRGBColorSpace;
    this.zahlenTextur.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    this.zahlenNetz = new THREE.Mesh(
      g,
      new THREE.MeshBasicMaterial({ map: this.zahlenTextur, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 }),
    );
    this.zahlenZelle = { k_spalten, k_zellB, k_zellH };
    this.wendeli.add(this.zahlenNetz);
  }
  zahlenZeichnen() {
    const { k_spalten, k_zellB, k_zellH } = this.zahlenZelle;
    const c = this.zahlenBild.getContext("2d");
    c.clearRect(0, 0, 2048, 1024);
    c.fillStyle = k_farbe.ziffer;
    c.textAlign = "center";
    c.textBaseline = "middle";
    for (let n = 0; n <= 100; n++) {
      const t = this.zahl(n);
      // so gross wie moeglich, Breite und Hoehe der Zelle mit Rand
      let w_pt = k_zellH * 0.95;
      c.font = `bold ${w_pt}px Arial, Helvetica, sans-serif`;
      const w_breite = c.measureText(t).width;
      if (w_breite > k_zellB * 0.92) w_pt *= (k_zellB * 0.92) / w_breite;
      c.font = `bold ${w_pt}px Arial, Helvetica, sans-serif`;
      c.fillText(t, ((n % k_spalten) + 0.5) * k_zellB, (Math.floor(n / k_spalten) + 0.54) * k_zellH);
    }
    this.zahlenTextur.needsUpdate = true;
  }

  // ---------- Zoom ----------
  zoomen(p_faktor) {
    this.zoom = klammer(this.zoom * p_faktor, k_zoom[0], k_zoom[1]);
  }

  // ---------- Anschauen: frei drehen und hoch/runter ----------
  freiSchieben(p_da, p_dy, p_direkt = false) {
    const f = this.frei;
    if (!f) return;
    f.a += p_da;
    f.y = klammer(f.y + p_dy, this.wangeY(0) - 40, this.fensterY(100) + 50);
    f.direkt = p_direkt;
  }

  // ---------- Vollbild: echt, wo der Browser es kann, sonst das Feld ueber die ganze Seite ----------
  vollbild() {
    const w = this.wurzel;
    const w_echt = document.fullscreenElement || document.webkitFullscreenElement;
    if (w_echt === w) (document.exitFullscreen || document.webkitExitFullscreen).call(document);
    else if (w.classList.contains("vollbild")) {
      w.classList.remove("vollbild");
      document.documentElement.style.overflow = this.altesOverflow || "";
    } else if (w.requestFullscreen) w.requestFullscreen().catch(() => this.vollbildErsatz());
    else if (w.webkitRequestFullscreen) w.webkitRequestFullscreen();
    else this.vollbildErsatz();
    this.vollbildAnzeigen();
    w.focus({ preventScroll: true });
  }
  // iPhone: Safari kennt Vollbild nur fuer Videos, also wird das Feld fest ueber die Seite gelegt
  vollbildErsatz() {
    this.altesOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    this.wurzel.classList.add("vollbild");
    this.vollbildAnzeigen();
  }
  vollbildAnzeigen() {
    const w_an = (document.fullscreenElement || document.webkitFullscreenElement) === this.wurzel || this.wurzel.classList.contains("vollbild");
    this.dom.voll.textContent = w_an ? "✕" : "⛶";
    this.dom.voll.setAttribute("aria-label", w_an ? "Vollbild verlassen" : "Vollbild");
    this.dom.voll.title = w_an ? "Vollbild verlassen" : "Vollbild";
  }

  stufeAnzeigen() {
    for (const b of this.dom.stufen) b.classList.toggle("an", b.dataset.stufe === this.stufe);
    this.dom.sterne.textContent = `★ ${this.punkte[this.stufe] ?? 0}`;
  }

  // ---------- 3D ----------
  bauenSzene() {
    let r;
    try {
      r = new THREE.WebGLRenderer({ canvas: this.dom.canvas, antialias: true });
    } catch (e) {
      this.dom.laden.textContent = "Dieser Browser kann das Spiel leider nicht anzeigen.";
      throw e;
    }
    r.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer = r;
    this.szene = new THREE.Scene();
    this.szene.background = new THREE.Color(k_farbe.hintergrund);
    this.kamera = new THREE.PerspectiveCamera(30, 1, 1, 3000);
    // Licht wie in den Animationen (animationen/src/montage/Montage.tsx)
    this.szene.add(new THREE.AmbientLight(0xffffff, 0.45));
    this.szene.add(new THREE.HemisphereLight(0xffffff, 0xb8ad98, 0.7));
    const l1 = new THREE.DirectionalLight(0xffffff, 2.2);
    l1.position.set(300, 400, 220);
    const l2 = new THREE.DirectionalLight(0xffffff, 0.7);
    l2.position.set(-100, 150, -300);
    this.szene.add(l1, l2);
    this.wendeli = new THREE.Group(); // dreht sich, Kamera steht
    this.szene.add(this.wendeli);

    this.beobachterGroesse = new ResizeObserver(() => this.groesse());
    this.beobachterGroesse.observe(this.wurzel);
    this.sichtbar = true;
    this.beobachterSicht = new IntersectionObserver((e) => {
      this.sichtbar = e[0].isIntersecting;
      // Aus dem Bild gescrollt: keine Zuege aufstauen, die Pfeiltasten gehoeren wieder der Seite
      if (!this.sichtbar) {
        this.zuege = [];
        if (document.activeElement === this.wurzel) this.wurzel.blur();
      }
    });
    this.beobachterSicht.observe(this.wurzel);
    this.uhr = new THREE.Clock();
    this.renderer.setAnimationLoop(() => this.bild());
  }

  groesse() {
    const b = this.wurzel.clientWidth, h = this.wurzel.clientHeight;
    if (!b || !h) return;
    this.renderer.setSize(b, h, false);
    this.kamera.aspect = b / h;
    this.kamera.updateProjectionMatrix();
    const k = this.dom.konfetti;
    k.width = b * Math.min(window.devicePixelRatio || 1, 2);
    k.height = h * Math.min(window.devicePixelRatio || 1, 2);
  }

  async modellLaden(p_url) {
    try {
      const w_antwort = await fetch(p_url);
      if (!w_antwort.ok) throw new Error(`${w_antwort.status}`);
      const w_gesamt = +w_antwort.headers.get("content-length") || 0;
      const w_leser = w_antwort.body.getReader();
      const w_teile = [];
      let w_da = 0;
      for (;;) {
        const { done, value } = await w_leser.read();
        if (done) break;
        w_teile.push(value);
        w_da += value.length;
        // gzip vom CDN: content-length ist dann die gepackte Groesse, darum gedeckelt
        if (w_gesamt) this.dom.laden.textContent = `Wendeli wird geladen … ${Math.min(99, Math.round((100 * w_da) / w_gesamt))} %`;
      }
      const w_buf = new Uint8Array(w_da);
      let o = 0;
      for (const t of w_teile) {
        w_buf.set(t, o);
        o += t.length;
      }
      const { teile, masse } = glbLesen(w_buf.buffer);
      this.masseSetzen(masse);
      this.modellBauen(teile);
      this.dom.laden.textContent = "";
      for (const b of this.dom.wahl) b.disabled = false;
    } catch (e) {
      this.dom.laden.textContent = "Das Wendeli konnte nicht geladen werden.";
      console.error("Wendeli-Spiel:", e);
    }
  }

  masseSetzen(M) {
    this.M = M;
    this.halb = M.k_r_inkreis * Math.tan(Math.PI / 10); // halbe Facettenbreite
    this.spurR = M.k_r_inkreis + 0.5; // die Spur knapp vor der Facette
    this.spurHalb = this.spurR * Math.tan(Math.PI / 10); // Facettenkante auf diesem Radius
    this.liftU = -(this.halb - 1.0); // Liftschacht am Facettenrand, wie in Treppe.tsx
    this.wangeUnter = M.k_fen_h / 2 + M.k_wange_abst + M.k_wange_b / 2;
  }
  fensterY(n) {
    return this.M.k_z_mitte0 + n * this.M.k_stufe_z;
  }
  wangeY(n) {
    return this.fensterY(n) - this.wangeUnter;
  }
  // Punkt auf Facette n (Facette = Einerstelle), u tangential, y hoch (Treppe.tsx)
  punkt(n, u, y, r = this.spurR) {
    const phi = n * k_teilung;
    return new THREE.Vector3(r * Math.cos(phi) - u * Math.sin(phi), y, -r * Math.sin(phi) - u * Math.cos(phi));
  }
  mitte(n) {
    return this.punkt(n, 0, this.wangeY(n));
  }
  // Die Punkte eines Zuges von n aus: Stufe auf der Wange, Lift am Facettenrand
  zugPunkte(n, z) {
    const h = this.spurHalb;
    if (z === 1) return [this.mitte(n), this.punkt(n, h, this.wangeY(n)), this.punkt(n + 1, -h, this.wangeY(n + 1)), this.mitte(n + 1)];
    if (z === -1) return this.zugPunkte(n - 1, 1).reverse();
    if (z === 10) {
      return [this.mitte(n), this.punkt(n, this.liftU, this.wangeY(n)), this.punkt(n, this.liftU, this.wangeY(n + 10)), this.mitte(n + 10)];
    }
    return this.zugPunkte(n - 10, 10).reverse();
  }

  modellBauen(T) {
    const mat = (p_farbe, p_rau = 0.6, p_flach = true) =>
      new THREE.MeshStandardMaterial({ color: p_farbe, roughness: p_rau, metalness: 0, flatShading: p_flach });
    const teil = (p_name, p_mat, p_knick = false) => {
      if (!T[p_name]) return;
      this.wendeli.add(new THREE.Mesh(p_knick ? knickNormalen(T[p_name], k_knick) : T[p_name], p_mat));
    };
    // Rohr: alles eben, flach schattiert braucht keine Normalen
    teil("rohr_koerper", mat(k_farbe.rohr, 0.55));
    teil("rohr_fenster", mat(k_farbe.fenster));
    teil("rohr_marken", mat(k_farbe.marke));
    this.zifferMat = mat(k_farbe.ziffer); // im Dezimal-Modus fensterfarben, siehe zahlenAnwenden()
    teil("rohr_ziffern", this.zifferMat);
    teil("rohr_wange", mat(k_farbe.wange));
    // Kappen: rund, darum Normalen mit Knick
    teil("fuss_zwinge", mat(k_farbe.zwinge, 0.45, false), true);
    teil("fuss_gummi", mat(k_farbe.gummi, 0.95, false), true);
    teil("spitze_holz", mat(k_farbe.rohr, 0.6, false), true); // Rohr und Spitze sind immer gleichfarbig
    teil("spitze_mine", mat(k_farbe.mine, 0.4, false), true);

    // Spur: Zylinder je Strecke und Kugeln an den Knicken, als Instanzen
    this.spur = new Spur(this.wendeli, k_farbe.spur);
    // Kopf der Spur
    this.kopf = new THREE.Mesh(
      new THREE.SphereGeometry(1.5, 20, 14),
      new THREE.MeshStandardMaterial({ color: "#FFF3C4", emissive: k_farbe.spur, emissiveIntensity: 1.2 }),
    );
    this.wendeli.add(this.kopf);
    // Rahmen um das Fenster, auf dem man steht (weiss), und das Ziel (gold, erst wenn man dort steht)
    this.rahmen = this.rahmenBauen("#FFFFFF", 0.35);
    this.zielRahmen = this.rahmenBauen("#FFC21A", 1.0);
    this.zielGlut = new THREE.Mesh(
      new THREE.CircleGeometry(this.M.k_fen_h / 2, 10),
      new THREE.MeshBasicMaterial({ color: "#FFE680", transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }),
    );
    this.wendeli.add(this.zielGlut);
    this.zielLicht = new THREE.PointLight("#FFD45A", 0, 28, 2);
    this.wendeli.add(this.zielLicht);
    // Sterne fuer den Tipp
    this.sternBild = sternTextur();
    this.kabineBauen();
    this.sterne = [];
    this.bereit = true;
    this.zahlenAnwenden();
    this.groesse();
    // Vorschau hinter dem Startbild: das Wendeli steht schon da
    this.pos = 34;
    this.kameraStand = { a: this.pos, y: this.wangeY(this.pos) + 4, abstand: 260, aZiel: this.pos, yZiel: this.wangeY(this.pos) + 4, abstandZiel: 260 };
    this.kopf.visible = false;
    this.rahmen.visible = false;
  }

  rahmenBauen(p_farbe, p_glanz) {
    const m = new THREE.Mesh(
      new THREE.TorusGeometry(this.M.k_fen_h / 2 + 1.1, 0.5, 10, 10),
      new THREE.MeshStandardMaterial({ color: p_farbe, emissive: p_farbe, emissiveIntensity: p_glanz, roughness: 0.4, transparent: true, opacity: 1 }),
    );
    this.wendeli.add(m);
    m.visible = false;
    return m;
  }
  // Ein Mesh vor das Fenster n stellen, Ecke unten wie das Fenster
  vorFenster(p_mesh, n, p_r) {
    const phi = n * k_teilung;
    p_mesh.position.set(p_r * Math.cos(phi), this.fensterY(n), -p_r * Math.sin(phi));
    p_mesh.rotation.set(0, phi + Math.PI / 2, Math.PI / 10);
  }

  // ---------- Ablauf ----------
  starten(p_stufe) {
    if (!this.bereit) return;
    this.ton.entsperren();
    this.dom.start.style.display = "none";
    this.wurzel.classList.add("aktiv"); // erst jetzt gehoert ein Wisch dem Spiel und nicht der Seite
    this.laeuft = true;
    this.pos = 0; // jede Stufe beginnt unten (Kaspar 04.11.26)
    // Blickfacette: zaehlt nur die Stufen, damit eine Liftfahrt das Wendeli nicht eine Runde dreht
    this.blick = this.pos;
    this.zuege = [];
    this.anim = null;
    // Kamera vom Drehen im Startbild auf die naechste gleichwertige Lage holen, nicht ganze Umgaenge zurueckdrehen
    const ks = this.kameraStand;
    ks.a = this.pos + ((((ks.a - this.pos) % 10) + 15) % 10) - 5;
    this.stufeWechseln(p_stufe);
    this.wurzel.focus({ preventScroll: true });
    if (!this.schauen) this.ton.sprechen("start");
  }

  stufeWechseln(p_stufe) {
    if (!this.laeuft) return;
    this.stufe = p_stufe;
    this.stufeAnzeigen();
    this.schauen = p_stufe === "anschauen";
    this.wurzel.classList.toggle("schauen", this.schauen);
    this.hilfeAktualisieren();
    if (this.schauen) {
      // Anschauen: kein Kopf, keine Spur, keine Aufgabe; die Kamera gehoert dem Finger
      this.zuege = [];
      this.anim = null;
      this.kabine.g.visible = this.kabine.schienen.visible = false;
      for (const st of this.sterne) this.wendeli.remove(st);
      this.sterne = [];
      this.tipp = null;
      this.kopf.visible = this.rahmen.visible = this.zielRahmen.visible = false;
      this.zielGlut.material.opacity = 0;
      this.zielLicht.intensity = 0;
      this.spur.setzen([]);
      this.erledigt = false; // ein laufender Erfolgs-Timer legt sonst eine Aufgabe an
      this.frei = { a: this.kameraStand.a, y: this.kameraStand.y };
      return;
    }
    this.kopf.visible = this.rahmen.visible = true;
    if (this.frei) {
      // vom Anschauen zurueck: auf 0, und zwar auf der Zehnerfacette, die gerade vorn ist
      this.pos = 0;
      this.blick = Math.round(this.kameraStand.a / 10) * 10;
      this.frei = null;
    }
    this.aufNull();
  }

  // Zurueck auf 0 und neu anfangen: beim Stufenwechsel und mit dem Knopf "↺ 0",
  // falls ein Kind irgendwo nicht mehr weiterweiss. Die Kamera faehrt hinunter.
  aufNull() {
    if (!this.laeuft) return;
    if (this.schauen) {
      // beim Anschauen: zurueck zur 0, auf die naechste Zehnerfacette gedreht
      const w_rest = ((Math.round(this.frei.a) % 10) + 10) % 10;
      this.frei = { a: Math.round(this.frei.a) + (w_rest > 5 ? 10 - w_rest : -w_rest), y: this.wangeY(0) + 4 };
      return;
    }
    this.zuege = [];
    this.anim = null;
    this.kabine.g.visible = this.kabine.schienen.visible = false; // falls mitten in einer Liftfahrt
    // die naechste Zehnerfacette, damit das Wendeli hoechstens eine halbe Runde dreht
    const w_einer = this.pos % 10;
    this.blick += w_einer > 5 ? 10 - w_einer : -w_einer;
    this.pos = 0;
    this.neueAufgabe();
  }

  neueAufgabe() {
    this.aufgabe = aufgabeErzeugen(this.stufe, this.pos, this.aufgabe);
    for (const st of this.sterne) this.wendeli.remove(st); // ein alter Tipp zeigt aufs alte Ziel
    this.sterne = [];
    this.tipp = null;
    this.gelaufen = { zehner: 0, einer: 0, zuege: 0 };
    this.spurPunkte = [this.mitte(this.pos)];
    this.spur.setzen(this.spurPunkte);
    this.zielSeit = null;
    this.erledigt = false;
    this.dom.aufgabeText.textContent = this.aufgabeText(this.aufgabe);
    this.dom.aufgabe.classList.remove("neu");
    void this.dom.aufgabe.offsetWidth;
    this.dom.aufgabe.classList.add("neu");
    this.standZeigen(false);
  }

  standZeigen(p_puls = true) {
    const g = this.gelaufen;
    this.dom.standZahl.textContent = this.mitVorzeichen(g.zehner + g.einer);
    // Zerlegt in Lift und Treppe: "+20 −1"
    this.dom.standTeile.innerHTML =
      g.zehner && g.einer ? `<i class="z">${this.mitVorzeichen(g.zehner)}</i> <i class="e">${this.mitVorzeichen(g.einer)}</i>` : "";
    if (p_puls) {
      this.dom.stand.classList.remove("puls");
      void this.dom.stand.offsetWidth;
      this.dom.stand.classList.add("puls");
    }
  }

  // Welche Zahl liegt unter diesem Bildschirmpunkt? Der Strahl wird nicht gegen
  // die 170 000 Dreiecke geschossen, sondern gegen die zehn Facettenebenen:
  // die Aussenflaeche ist buendig, das Zehneck ist also die ganze Wahrheit.
  zahlBeiPunkt(p_x, p_y) {
    const r = this.dom.canvas.getBoundingClientRect();
    const w_ndc = new THREE.Vector2(((p_x - r.left) / r.width) * 2 - 1, -((p_y - r.top) / r.height) * 2 + 1);
    const w_strahl = new THREE.Raycaster();
    w_strahl.setFromCamera(w_ndc, this.kamera);
    this.wendeli.updateMatrixWorld();
    const w_inv = this.wendeli.matrixWorld.clone().invert();
    const o = w_strahl.ray.origin.clone().applyMatrix4(w_inv);
    const d = w_strahl.ray.direction.clone().transformDirection(w_inv);
    let w_best = null;
    for (let i = 0; i < 10; i++) {
      const phi = i * k_teilung;
      const N = new THREE.Vector3(Math.cos(phi), 0, -Math.sin(phi)); // Facettennormale
      const T = new THREE.Vector3(-Math.sin(phi), 0, -Math.cos(phi)); // tangential, +u
      const w_nd = N.dot(d);
      if (w_nd >= 0) continue; // Rueckseite
      const t = (this.M.k_r_inkreis - N.dot(o)) / w_nd;
      if (t <= 0) continue;
      const p = o.clone().addScaledVector(d, t);
      if (Math.abs(T.dot(p)) > this.halb + 0.01) continue;
      if (!w_best || t < w_best.t) w_best = { t, i, y: p.y };
    }
    if (!w_best || w_best.y < 0 || w_best.y > this.M.k_laenge_rohr) return null;
    // Facette i traegt i, i+10, i+20 ...; das naechste Fenster in der Hoehe
    const n = w_best.i + 10 * Math.round((w_best.y - this.fensterY(w_best.i)) / this.M.k_umgang_z);
    return n >= 0 && n <= 100 ? n : null;
  }

  // Auf eine Zahl getippt: dorthin laufen, erst mit dem Lift, dann die Treppe
  // (Kaspar 04.11.26). Ersetzt, was noch in der Warteschlange stand.
  zahlAntippen(p_x, p_y) {
    if (!this.laeuft || this.erledigt || !this.bereit || this.schauen) return;
    const n = this.zahlBeiPunkt(p_x, p_y);
    if (n === null) return;
    const w_von = this.anim && this.anim.typ === "zug" ? this.anim.nach : this.pos;
    this.zuege = wegLiftZuerst(w_von, n);
    this.ton.weiter();
  }

  // Ein Zug von der Tastatur oder vom Wischen: in die Warteschlange
  zug(z) {
    if (!this.laeuft || this.erledigt) return; // waehrend des Konfettis kurz Pause
    if (this.zuege.length > 3) return; // nicht endlos vorauslaufen
    this.zuege.push(z);
  }

  // Den naechsten Zug aus der Warteschlange beginnen
  zugBeginnen() {
    const z = this.zuege.shift();
    const w_nach = this.pos + z;
    if (w_nach < 0 || w_nach > 100) {
      // Rand: kurz anstossen, nicht weiter
      this.anim = { typ: "anstoss", z, t: 0, dauer: 0.3 };
      this.ton.piep(150, 0.15, "square", 0.04);
      this.ton.sprechen("halt", 4);
      return;
    }
    const w_punkte = this.zugPunkte(this.pos, z);
    const w_l = w_punkte.slice(1).map((p, i) => p.distanceTo(w_punkte[i]));
    const w_dauer = (Math.abs(z) === 10 ? k_lift_s : k_schritt_s) / (this.zuege.length > 0 ? 1.8 : 1);
    this.anim = { typ: "zug", z, von: this.pos, nach: w_nach, punkte: w_punkte, laengen: w_l, gesamt: w_l.reduce((s, v) => s + v, 0), t: 0, dauer: w_dauer };
    this.zielSeit = null;
    this.ton.schritt(z);
  }

  zugBeenden() {
    const a = this.anim;
    this.pos = a.nach;
    this.spurPunkte.push(...a.punkte.slice(1));
    this.spur.setzen(this.spurPunkte);
    if (Math.abs(a.z) === 10) this.gelaufen.zehner += a.z;
    else {
      this.gelaufen.einer += a.z;
      this.blick += a.z; // nur eine Stufe dreht das Wendeli, der Lift nicht
    }
    this.gelaufen.zuege += 1;
    this.standZeigen();
    this.anim = null;
  }

  // Lift beim Anteil s: drei Teilstrecken mit eigener Zeit, der Kopf huepft
  // hinein und hinaus, dazwischen faehrt die Kabine (Kaspar 04.11.26)
  liftBei(a, s) {
    const [p1, p2] = k_lift_phasen;
    const glatt = (x) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);
    const q = a.punkte;
    let w_bis, w_hupf = 0, w_hoch;
    if (s < p1) {
      const f = glatt(s / p1);
      w_bis = [q[0], q[0].clone().lerp(q[1], f)];
      w_hupf = Math.sin(Math.PI * f) * 2.4;
      w_hoch = 0;
    } else if (s < p2) {
      w_hoch = glatt((s - p1) / (p2 - p1));
      w_bis = [q[0], q[1], q[1].clone().lerp(q[2], w_hoch)];
    } else {
      const f = glatt((s - p2) / (1 - p2));
      w_bis = [q[0], q[1], q[2], q[2].clone().lerp(q[3], f)];
      w_hupf = Math.sin(Math.PI * f) * 2.4;
      w_hoch = 1;
    }
    const w_kopf = w_bis[w_bis.length - 1].clone();
    w_kopf.y += w_hupf;
    // Kabine: blendet waehrend des Hineinhuepfens ein, waehrend des Hinaushuepfens aus
    const w_sicht = Math.min(klammer(s / (p1 * 0.6), 0, 1), klammer((1 - s) / ((1 - p2) * 0.6), 0, 1));
    return { bis: w_bis, kopf: w_kopf, hoch: w_hoch, kabineY: q[1].y + (q[2].y - q[1].y) * w_hoch, sicht: w_sicht };
  }

  // Minimale Liftkabine: Boden, Dach, zwei Pfosten, dazu zwei Schienen im Schacht
  kabineBauen() {
    const mat = new THREE.MeshStandardMaterial({ color: k_farbe.spur, emissive: k_farbe.spur, emissiveIntensity: 0.35, roughness: 0.4, transparent: true });
    const hell = new THREE.MeshStandardMaterial({ color: "#FFFFFF", roughness: 0.5, transparent: true });
    const schiene = new THREE.MeshBasicMaterial({ color: "#FFFFFF", transparent: true, depthWrite: false });
    const g = new THREE.Group();
    const kiste = (b, h, t, x, y, z, m) => {
      const k = new THREE.Mesh(new THREE.BoxGeometry(b, h, t), m);
      k.position.set(x, y, z);
      g.add(k);
    };
    // lokal: x radial nach aussen, z entlang der Facette, y hoch; der Kopf (r 1.5) steht auf dem Boden
    const wand = new THREE.MeshStandardMaterial({ color: "#FFFFFF", roughness: 0.6, transparent: true, depthWrite: false });
    wand.userData.anteil = 0.45; // halb durchsichtig: man sieht das Rohr dahinter
    kiste(3.6, 0.55, 5.6, 0, -1.85, 0, mat);
    kiste(3.6, 0.45, 5.6, 0, 2.7, 0, mat);
    kiste(0.4, 4.5, 0.4, -0.4, 0.42, 2.6, hell);
    kiste(0.4, 4.5, 0.4, -0.4, 0.42, -2.6, hell);
    kiste(0.15, 4.5, 5.2, -1.6, 0.42, 0, wand);
    const schienen = new THREE.Group();
    for (const z of [3.1, -3.1]) {
      const r = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 1, 6), schiene);
      r.position.z = z;
      r.position.x = -0.6;
      schienen.add(r);
    }
    this.wendeli.add(g, schienen);
    g.visible = schienen.visible = false;
    this.kabine = { g, schienen, mats: [mat, hell, wand], schiene };
  }

  // Kabine und Schienen fuer den laufenden Lift setzen
  kabineSetzen(a, l) {
    const k = this.kabine;
    const phi = a.von * k_teilung; // von und nach liegen auf derselben Facette
    const p = this.punkt(a.von, this.liftU, l.kabineY);
    k.g.visible = k.schienen.visible = l.sicht > 0;
    k.g.position.copy(p);
    k.g.rotation.set(0, phi, 0);
    for (const m of k.mats) m.opacity = l.sicht * (m.userData.anteil ?? 1);
    // Schienen ueber den ganzen Fahrweg plus etwas Ueberstand
    const y0 = Math.min(a.punkte[1].y, a.punkte[2].y) - 4, y1 = Math.max(a.punkte[1].y, a.punkte[2].y) + 5;
    const q = this.punkt(a.von, this.liftU, (y0 + y1) / 2);
    k.schienen.position.copy(q);
    k.schienen.rotation.set(0, phi, 0);
    k.schienen.scale.set(1, y1 - y0, 1);
    k.schiene.opacity = 0.55 * l.sicht;
  }

  // Punkt auf dem Zug beim Anteil s (nach Weglaenge)
  zugBei(a, s) {
    let w_rest = s * a.gesamt;
    const w_bis = [a.punkte[0]];
    for (let i = 0; i < a.laengen.length; i++) {
      if (w_rest >= a.laengen[i]) {
        w_bis.push(a.punkte[i + 1]);
        w_rest -= a.laengen[i];
        continue;
      }
      w_bis.push(a.punkte[i].clone().lerp(a.punkte[i + 1], a.laengen[i] ? w_rest / a.laengen[i] : 0));
      break;
    }
    return w_bis;
  }

  erfolg() {
    this.erledigt = true;
    this.punkte[this.stufe] += 1;
    this.speichern();
    this.stufeAnzeigen();
    // Kuerzester Weg gelaufen, und es gab einen kuerzeren als das Abzaehlen der Ziffern?
    const d = this.aufgabe.ziel - this.aufgabe.start;
    const w_kurz = kuerzesterWeg(this.aufgabe.start, this.aufgabe.ziel).length;
    if (this.gelaufen.zuege === w_kurz && w_kurz < ziffernZuege(d)) this.ton.sprechen("lob_kurz");
    else {
      let w;
      do w = k_ton_lob[zufall(0, k_ton_lob.length - 1)];
      while (w === this.letztesLob);
      this.letztesLob = w;
      this.ton.sprechen(w);
    }
    this.ton.piep(1046, 0.18, "sine", 0.05);
    const p = this.zielRahmen.getWorldPosition(new THREE.Vector3()).project(this.kamera);
    this.konfetti((p.x + 1) / 2, (1 - p.y) / 2);
    // Neue Aufgabe von hier aus, wenn das Konfetti fliegt
    setTimeout(() => {
      if (this.laeuft && this.erledigt && !this.schauen) this.neueAufgabe();
    }, 1400);
  }

  // ---------- Tipp: Sternenspur auf dem kuerzesten Weg ----------
  tippZeigen() {
    if (!this.laeuft || this.erledigt || this.schauen) return;
    // Ab der Lage, in der man steht, wenn laufender Zug und Warteschlange fertig sind
    let w_von = this.anim && this.anim.typ === "zug" ? this.anim.nach : this.pos;
    let b = this.blick + (this.anim && this.anim.typ === "zug" && Math.abs(this.anim.z) === 1 ? this.anim.z : 0);
    for (const z of this.zuege) {
      if (w_von + z < 0 || w_von + z > 100) continue; // wird nur angestossen
      w_von += z;
      if (Math.abs(z) === 1) b += z;
    }
    const w_zuege = kuerzesterWeg(w_von, this.aufgabe.ziel);
    this.ton.sprechen("tipp", 3);
    for (const s of this.sterne) this.wendeli.remove(s);
    this.sterne = [];
    if (!w_zuege.length) return;
    // Der Weg als Punkte mit Blickfacette a: eine Stufe dreht um eine Facette, der Lift nicht
    let n = w_von;
    const w_pkt = [{ p: this.mitte(n), a: b }];
    for (const z of w_zuege) {
      const q = this.zugPunkte(n, z);
      const w_a = Math.abs(z) === 1 ? [b + z / 2, b + z / 2, b + z] : [b, b, b];
      for (let i = 1; i < q.length; i++) w_pkt.push({ p: q[i], a: w_a[i - 1] });
      n += z;
      if (Math.abs(z) === 1) b += z;
    }
    const w_lang = [0];
    for (let i = 1; i < w_pkt.length; i++) w_lang.push(w_lang[i - 1] + w_pkt[i].p.distanceTo(w_pkt[i - 1].p));
    const w_gesamt = w_lang[w_lang.length - 1];
    // Sterne in gleichen Abstaenden, leicht vor der Spur
    const k_abstand = 2.4;
    // gleichmaessig verteilt, und der letzte Stern sitzt genau auf dem Ziel
    const w_n_sterne = Math.max(1, Math.round(w_gesamt / k_abstand));
    for (let i = 0; i <= w_n_sterne; i++) {
      const s = (w_gesamt * i) / w_n_sterne;
      const p = this.tippBei(w_pkt, w_lang, s).p;
      const phi = Math.atan2(-p.z, p.x);
      p.x += 0.8 * Math.cos(phi);
      p.z -= 0.8 * Math.sin(phi);
      const st = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.sternBild, transparent: true, depthWrite: false, opacity: 0 }));
      st.position.copy(p);
      st.userData.s = s;
      st.scale.setScalar(0.01);
      this.wendeli.add(st);
      this.sterne.push(st);
    }
    // Die Spur waechst in 1 bis 2.2 s bis zum Ziel, die Kamera fliegt ihrer Spitze nach
    const w_wachs = klammer(w_gesamt / 70, 1.0, 2.2);
    this.tipp = { pkt: w_pkt, lang: w_lang, gesamt: w_gesamt, seit: this.uhr.elapsedTime, wachs: w_wachs, dauer: w_wachs + k_tipp_dauer - 1.0 };
  }

  // Punkt und Blickfacette auf dem Tippweg bei der Weglaenge s
  tippBei(p_pkt, p_lang, s) {
    let i = 1;
    while (i < p_lang.length - 1 && p_lang[i] < s) i++;
    const l = p_lang[i] - p_lang[i - 1];
    const f = l > 0 ? klammer((s - p_lang[i - 1]) / l, 0, 1) : 1;
    return { p: p_pkt[i - 1].p.clone().lerp(p_pkt[i].p, f), a: p_pkt[i - 1].a + (p_pkt[i].a - p_pkt[i - 1].a) * f };
  }

  // ---------- Konfetti ----------
  konfetti(p_x, p_y) {
    const k = this.dom.konfetti;
    const f = ["#FFC21A", "#EE8A2E", "#25509E", "#E3392B", "#22A55E", "#FF6F91", "#FFFFFF"];
    const w_neu = Array.from({ length: 140 }, () => {
      const w = -Math.PI / 2 + (Math.random() - 0.5) * 2.2;
      const v = 0.35 + Math.random() * 0.75;
      return {
        x: p_x * k.width, y: p_y * k.height,
        vx: Math.cos(w) * v * k.height, vy: Math.sin(w) * v * k.height,
        dreh: Math.random() * 6, vd: (Math.random() - 0.5) * 14,
        farbe: f[zufall(0, f.length - 1)], g: (4 + Math.random() * 5) * (k.height / 600), t: 0,
      };
    });
    this.konfettiTeile = (this.konfettiTeile || []).concat(w_neu);
  }
  konfettiBild(dt) {
    const k = this.dom.konfetti;
    const c = k.getContext("2d");
    c.clearRect(0, 0, k.width, k.height);
    if (!this.konfettiTeile || !this.konfettiTeile.length) return;
    for (const p of this.konfettiTeile) {
      p.t += dt;
      p.vy += k.height * 1.1 * dt;
      p.vx *= 1 - 1.2 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.dreh += p.vd * dt;
      c.save();
      c.globalAlpha = Math.max(0, 1 - Math.max(0, p.t - 1.6) / 0.8);
      c.translate(p.x, p.y);
      c.rotate(p.dreh);
      c.fillStyle = p.farbe;
      c.fillRect(-p.g, -p.g * 0.45, p.g * 2, p.g * 0.9 * Math.abs(Math.cos(p.dreh * 1.3)) + 1);
      c.restore();
    }
    this.konfettiTeile = this.konfettiTeile.filter((p) => p.t < 2.4 && p.y < k.height + 40);
  }

  // ---------- je Bild ----------
  bild() {
    const dt = Math.min(this.uhr.getDelta(), 0.1);
    const t = this.uhr.elapsedTime;
    if (!this.bereit || !this.sichtbar) return;

    let w_kopf = this.mitte(this.pos);
    let w_a = this.blick ?? this.pos; // Blickfacette, stetig
    let w_y = this.wangeY(this.pos);
    if (this.laeuft && !this.schauen) {
      if (!this.anim && this.zuege.length) this.zugBeginnen();
      const a = this.anim;
      if (a) {
        a.t += dt;
        const s = klammer(a.t / a.dauer, 0, 1);
        const e = s < 0.5 ? 2 * s * s : 1 - Math.pow(-2 * s + 2, 2) / 2;
        if (a.typ === "zug" && Math.abs(a.z) === 10) {
          const l = this.liftBei(a, s);
          w_kopf = l.kopf;
          this.spur.setzen(this.spurPunkte.concat(l.bis.slice(1)));
          this.kabineSetzen(a, l);
          w_y = this.wangeY(a.von) + (this.wangeY(a.nach) - this.wangeY(a.von)) * l.hoch;
          if (s >= 1) {
            this.kabine.g.visible = this.kabine.schienen.visible = false;
            this.zugBeenden();
          }
        } else if (a.typ === "zug") {
          const w_bis = this.zugBei(a, e);
          w_kopf = w_bis[w_bis.length - 1];
          this.spur.setzen(this.spurPunkte.concat(w_bis.slice(1)));
          w_a = this.blick + a.z * e;
          w_y = this.wangeY(a.von) + (this.wangeY(a.nach) - this.wangeY(a.von)) * e;
          if (s >= 1) this.zugBeenden();
        } else {
          // anstossen: ein kurzes Stueck in die Richtung und zurueck
          const w_hub = Math.sin(Math.PI * s) * 1.6;
          const w_dir = Math.abs(a.z) === 10 ? new THREE.Vector3(0, Math.sign(a.z), 0) : this.punkt(this.pos, Math.sign(a.z), 0).sub(this.punkt(this.pos, 0, 0)).normalize();
          w_kopf = this.mitte(this.pos).add(w_dir.multiplyScalar(w_hub));
          if (s >= 1) this.anim = null;
        }
      }
      // Auf dem Ziel? Dann nach 1 s leuchten, nach 3 s Konfetti
      // Anstossen am Rand verlaesst das Feld nicht und haelt die Uhr nicht an
      const w_steht =
        (!this.anim || this.anim.typ === "anstoss") &&
        this.zuege.every((z) => this.pos + z < 0 || this.pos + z > 100) &&
        this.pos === this.aufgabe.ziel &&
        !this.erledigt;
      if (w_steht && this.zielSeit === null) this.zielSeit = t;
      if (!w_steht && !this.erledigt) this.zielSeit = null;
      const w_dort = this.zielSeit === null ? 0 : t - this.zielSeit;
      const w_glut = this.erledigt ? Math.max(0, 1 - (t - this.erfolgSeit) / 1.2) : klammer((w_dort - k_leuchten_ab) / 0.5, 0, 1);
      if (w_steht && w_dort >= k_konfetti_ab) {
        this.erfolgSeit = t;
        this.erfolg();
      }
      const w_puls = 0.75 + 0.25 * Math.sin(t * 7);
      this.zielRahmen.visible = w_glut > 0;
      this.vorFenster(this.zielRahmen, this.aufgabe.ziel, this.M.k_r_inkreis + 0.6);
      this.zielRahmen.material.opacity = w_glut;
      this.zielRahmen.material.emissiveIntensity = 1.4 * w_puls;
      this.zielRahmen.scale.setScalar(1 + 0.08 * w_glut * Math.sin(t * 7));
      this.vorFenster(this.zielGlut, this.aufgabe.ziel, this.M.k_r_inkreis + 0.08);
      // Die Zahl muss lesbar bleiben: Glut und Licht nur so stark, dass das Fenster warm wird
      this.zielGlut.material.opacity = 0.22 * w_glut * w_puls;
      this.vorFenster(this.zielLicht, this.aufgabe.ziel, this.M.k_r_inkreis + 9);
      this.zielLicht.intensity = 160 * w_glut * w_puls;
      // Der weisse Rahmen zeigt die Zahl, auf der man steht
      // Bei Stufe und Lift springt er auf halbem Weg auf die neue Zahl - beim Lift
      // direkt, sonst zoege er durch neun Fenster einmal um das Rohr
      const w_n_rahmen = this.anim && this.anim.typ === "zug" && this.anim.t / this.anim.dauer >= 0.5 ? this.anim.nach : this.pos;
      this.vorFenster(this.rahmen, w_n_rahmen, this.M.k_r_inkreis + 0.5);
      this.rahmen.visible = w_glut < 0.5;
      this.kopf.position.copy(w_kopf);
    }

    // Tipp: Sterne wachsen der Spur entlang, stehen, verglimmen
    // Kamera: folgt dem Kopf, beim Tipp der Spitze der Sternenspur
    const ks = this.kameraStand;
    // Hochformat: die eigene Facette ganz, die Nachbarn angeschnitten - sonst werden die Ziffern zu klein
    const w_quer = 24 / Math.min(this.kamera.aspect, 1);
    let w_abstand = Math.max(150, w_quer / Math.tan((15 * Math.PI) / 180)) * this.zoom;
    ks.aZiel = w_a;
    ks.yZiel = w_y + 4;
    // Anschauen: die Kamera folgt dem Finger oder den Pfeiltasten
    if (this.schauen && this.frei) {
      ks.aZiel = this.frei.a;
      ks.yZiel = this.frei.y;
    }

    // Tipp: Sterne wachsen der Spur entlang, stehen am Ziel, verglimmen
    const tp = this.tipp;
    if (tp && this.sterne.length) {
      const w_s = t - tp.seit;
      // bis 4 mm ueber das Ziel hinaus, damit auch der letzte Stern ganz aufgeht
      const w_spitze = (tp.gesamt + 4) * klammer(w_s / tp.wachs, 0, 1);
      const w_aus = klammer((tp.dauer - w_s) / 0.6, 0, 1);
      const w_gross = 3.2 * Math.max(1, ks.abstand / 170);
      for (const st of this.sterne) {
        const w_e = klammer((w_spitze - st.userData.s) / 4, 0, 1);
        st.material.opacity = w_e * w_aus;
        st.scale.setScalar(w_gross * w_e * (1 + 0.15 * Math.sin(t * 9 + st.position.y)));
        st.material.rotation = t * 1.5 + st.position.y;
      }
      // Kamera auf die Spitze, etwas weiter weg; gegen Ende zurueck zum Kopf
      const w_mit = klammer(Math.min(w_s / 0.3, (tp.dauer - w_s) / 0.5), 0, 1);
      const q = this.tippBei(tp.pkt, tp.lang, w_spitze);
      ks.aZiel = w_a + (q.a - w_a) * w_mit;
      ks.yZiel = ks.yZiel + (q.p.y + 4 - ks.yZiel) * w_mit;
      w_abstand *= 1 + 0.3 * w_mit;
      if (w_s > tp.dauer) {
        for (const st of this.sterne) this.wendeli.remove(st);
        this.sterne = [];
        this.tipp = null;
      }
    }
    ks.abstandZiel = w_abstand;
    // beim Ziehen fast direkt, sonst weich
    const w_k = 1 - Math.exp(-dt * (this.schauen && this.frei?.direkt ? 18 : 5));
    ks.a += (ks.aZiel - ks.a) * w_k;
    ks.y += (ks.yZiel - ks.y) * w_k;
    ks.abstand += (ks.abstandZiel - ks.abstand) * (1 - Math.exp(-dt * 8));
    if (!this.laeuft) ks.a = 34 + t * 0.12; // Startbild: das Wendeli dreht sich langsam
    this.wendeli.rotation.y = -ks.a * k_teilung;
    const w_hoehe = 0.12; // rad, leicht von oben
    this.kamera.position.set(ks.abstand * Math.cos(w_hoehe), ks.y + ks.abstand * Math.sin(w_hoehe), 0);
    this.kamera.lookAt(0, ks.y, 0);

    this.renderer.render(this.szene, this.kamera);
    this.konfettiBild(dt);
  }
}

// Spur als Instanzen: ein Zylinder je Strecke, eine Kugel je Knick, Kern und Schein
class Spur {
  constructor(p_eltern, p_farbe) {
    this.eltern = p_eltern;
    this.farbe = p_farbe;
    this.platz = 0;
    this.teile = [];
    this.bauen(256);
  }
  bauen(p_platz) {
    for (const t of this.teile) {
      this.eltern.remove(t.m);
      t.m.dispose();
    }
    this.platz = p_platz;
    const kern = new THREE.MeshStandardMaterial({ color: this.farbe, emissive: this.farbe, emissiveIntensity: 0.9 });
    const schein = new THREE.MeshBasicMaterial({ color: this.farbe, transparent: true, opacity: 0.3, depthWrite: false });
    const zyl = new THREE.CylinderGeometry(1, 1, 1, 10, 1, true);
    const kug = new THREE.SphereGeometry(1, 12, 8);
    this.teile = [
      { m: new THREE.InstancedMesh(zyl, kern, p_platz), r: 0.6, zyl: true },
      { m: new THREE.InstancedMesh(zyl, schein, p_platz), r: 1.5, zyl: true },
      { m: new THREE.InstancedMesh(kug, kern, p_platz), r: 0.6, zyl: false },
      { m: new THREE.InstancedMesh(kug, schein, p_platz), r: 1.5, zyl: false },
    ].map((t) => {
      t.m.frustumCulled = false;
      this.eltern.add(t.m);
      return t;
    });
  }
  setzen(p_punkte) {
    if (p_punkte.length + 2 > this.platz) {
      let w_platz = this.platz;
      while (p_punkte.length + 2 > w_platz) w_platz *= 2;
      this.bauen(w_platz);
    }
    const mat = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const oben = new THREE.Vector3(0, 1, 0);
    const d = new THREE.Vector3();
    const s = new THREE.Vector3();
    for (const t of this.teile) {
      let n = 0;
      for (let i = 0; i < p_punkte.length; i++) {
        if (t.zyl) {
          if (i === 0) continue;
          d.subVectors(p_punkte[i], p_punkte[i - 1]);
          const l = d.length();
          if (l < 1e-4) continue;
          q.setFromUnitVectors(oben, d.normalize());
          mat.compose(p_punkte[i - 1].clone().lerp(p_punkte[i], 0.5), q, s.set(t.r, l, t.r));
        } else {
          mat.compose(p_punkte[i], q.identity(), s.set(t.r, t.r, t.r));
        }
        t.m.setMatrixAt(n++, mat);
      }
      t.m.count = n;
      t.m.instanceMatrix.needsUpdate = true;
    }
  }
}

// Ein weicher, goldener Stern als Textur fuer die Tipp-Spur
const sternTextur = () => {
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const g = c.getContext("2d");
  const glanz = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  glanz.addColorStop(0, "rgba(255,246,200,0.95)");
  glanz.addColorStop(0.35, "rgba(255,212,90,0.45)");
  glanz.addColorStop(1, "rgba(255,177,59,0)");
  g.fillStyle = glanz;
  g.fillRect(0, 0, 128, 128);
  g.beginPath();
  for (let i = 0; i < 10; i++) {
    const r = i % 2 ? 18 : 44;
    const w = -Math.PI / 2 + (i * Math.PI) / 5;
    g.lineTo(64 + r * Math.cos(w), 64 + r * Math.sin(w));
  }
  g.closePath();
  g.fillStyle = "#FFD23F";
  g.strokeStyle = "#FFFFFF";
  g.lineWidth = 5;
  g.stroke();
  g.fill();
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
};

// ---------- Selbststart fuer jedes <div data-wendeli-spiel> ----------
const tonQuelle = (p_wert) => {
  if (!p_wert) return "ton/";
  try {
    return p_wert.trim().startsWith("{") ? JSON.parse(p_wert) : p_wert;
  } catch {
    return p_wert;
  }
};
export const alleStarten = (p_wurzel = document) => {
  for (const el of p_wurzel.querySelectorAll("[data-wendeli-spiel]")) {
    if (el.dataset.wendeliSpielLaeuft) continue;
    el.dataset.wendeliSpielLaeuft = "1";
    // je Instanz einzeln: ein Fehler (z.B. kein WebGL) haelt die anderen nicht auf
    try {
      el.wendeliSpiel = new WendeliSpiel(el, { modell: el.dataset.modell || "wendeli_spiel.glb", ton: tonQuelle(el.dataset.ton) });
    } catch (e) {
      console.error("Wendeli-Spiel:", e);
    }
  }
};
alleStarten();
// Shopify-Theme-Editor ersetzt Sektionen, ohne das Modul neu zu laden
document.addEventListener("shopify:section:load", (e) => alleStarten(e.target));
document.addEventListener("shopify:section:unload", (e) => {
  for (const el of e.target.querySelectorAll("[data-wendeli-spiel]")) el.wendeliSpiel?.zerstoeren();
});
